/**
 * Reine Berechnungs-Helfer fuer das Finanzen-Modul: Monats-/Jahres-Aggregation
 * von Buchungen (Summen, Kategorie-Verteilung, Monatsreihen). Bewusst ohne
 * Redux/React, damit Monats- und Jahresuebersicht dieselbe Logik teilen (DRY).
 * Da der gesicherte Modus zero-knowledge ist, laeuft jede Aggregation im Browser
 * auf den bereits entschluesselten Buchungen.
 */

import { TYP_EINNAHME } from './konstanten';

/** Heutiges Datum als ISO-Tagesstempel (YYYY-MM-DD) fuer Formular-Vorbelegung. */
export const heute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Betrag einer Buchung als positive Zahl (0, wenn nicht gepflegt). */
export const betragVon = (b) => (b.betrag != null ? Math.abs(Number(b.betrag)) : 0);

/** True, wenn die Buchung eine Einnahme ist. */
export const istEinnahme = (b) => b.typ === TYP_EINNAHME;

/** Monats-Schluessel (YYYY-MM) aus einem ISO-Datum. */
export const monatSchluessel = (datum) => (datum ? String(datum).slice(0, 7) : '');

/** Jahr (YYYY) aus einem ISO-Datum. */
export const jahrVon = (datum) => (datum ? String(datum).slice(0, 4) : '');

/** Menschenlesbares Monats-Label (z.B. "Juli 2026") aus YYYY-MM. */
export const monatLabel = (ym) => {
  if (!ym) return '';
  const [jahr, monat] = ym.split('-').map(Number);
  return new Date(jahr, monat - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

/** Verschiebt einen Monats-Schluessel um {@code delta} Monate (fuer die Navigation). */
export const verschiebeMonat = (ym, delta) => {
  const [jahr, monat] = ym.split('-').map(Number);
  const d = new Date(jahr, monat - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Alle Buchungen eines Monats (YYYY-MM), nach Datum absteigend sortiert. */
export const buchungenImMonat = (buchungen, ym) =>
  buchungen
    .filter((b) => monatSchluessel(b.datum) === ym)
    .slice()
    .sort((a, b) => String(b.datum).localeCompare(String(a.datum)));

/** Sortiert Buchungen nach Datum oder Betrag in der gewaehlten Richtung. */
export const sortiereBuchungen = (buchungen, feld, richtung) => {
  const faktor = richtung === 'asc' ? 1 : -1;
  return [...buchungen].sort((a, b) => {
    if (feld === 'betrag') return (betragVon(a) - betragVon(b)) * faktor;
    return String(a.datum).localeCompare(String(b.datum)) * faktor;
  });
};

/** Summen (Einnahmen, Ausgaben, Saldo) einer Buchungsliste. */
export const summen = (buchungen) => {
  let einnahmen = 0;
  let ausgaben = 0;
  buchungen.forEach((b) => {
    if (istEinnahme(b)) einnahmen += betragVon(b);
    else ausgaben += betragVon(b);
  });
  return { einnahmen, ausgaben, saldo: einnahmen - ausgaben };
};

/** Buchungen, die einem bestimmten Investitionsort (UUID) zugeordnet sind. */
export const buchungenFuerOrt = (buchungen, ortUuid) =>
  buchungen.filter((b) => b.ort === ortUuid);

/** Buchungen ohne zugeordneten Investitionsort. */
export const buchungenOhneOrt = (buchungen) => buchungen.filter((b) => !b.ort);

/**
 * Barvermoegen eines Ortes: Anfangsbestand plus Saldo (Einnahmen − Ausgaben)
 * aller ihm zugeordneten Buchungen.
 */
export const barvermoegenVon = (ort, buchungen) =>
  Number(ort.anfangsbestand || 0) + summen(buchungenFuerOrt(buchungen, ort.uuid)).saldo;

/** Ausgaben je Kategorie (absteigend) – Basis fuer den Donut. */
export const ausgabenJeKategorie = (buchungen) => {
  const map = {};
  buchungen
    .filter((b) => !istEinnahme(b))
    .forEach((b) => {
      const kat = b.kategorie || 'Sonstiges';
      map[kat] = (map[kat] || 0) + betragVon(b);
    });
  return Object.entries(map)
    .map(([kategorie, betrag]) => ({ kategorie, betrag }))
    .filter((e) => e.betrag > 0)
    .sort((a, b) => b.betrag - a.betrag);
};

/** Sortierte Liste aller Monate (YYYY-MM), in denen Buchungen existieren. */
export const vorhandeneMonate = (buchungen) =>
  [...new Set(buchungen.map((b) => monatSchluessel(b.datum)).filter(Boolean))].sort((a, b) =>
    b.localeCompare(a)
  );

/** Zwoelf Monatsreihen (Jan–Dez) eines Jahres mit Einnahmen/Ausgaben/Saldo. */
export const monatsReihenFuerJahr = (buchungen, jahr) => {
  const reihen = Array.from({ length: 12 }, (_, i) => ({
    monat: i + 1,
    label: new Date(jahr, i, 1).toLocaleDateString('de-DE', { month: 'short' }),
    einnahmen: 0,
    ausgaben: 0,
  }));
  buchungen
    .filter((b) => jahrVon(b.datum) === String(jahr))
    .forEach((b) => {
      const idx = Number(monatSchluessel(b.datum).split('-')[1]) - 1;
      if (idx < 0 || idx > 11) return;
      if (istEinnahme(b)) reihen[idx].einnahmen += betragVon(b);
      else reihen[idx].ausgaben += betragVon(b);
    });
  return reihen.map((r) => ({ ...r, saldo: r.einnahmen - r.ausgaben }));
};

/** Sortierte Liste aller Jahre (YYYY), in denen Buchungen existieren. */
export const vorhandeneJahre = (buchungen) =>
  [...new Set(buchungen.map((b) => jahrVon(b.datum)).filter(Boolean))].sort((a, b) =>
    b.localeCompare(a)
  );

/** Anzahl der einer Serie (Dauerauftrag-UUID) zugeordneten Buchungen. */
export const buchungenDerSerie = (buchungen, serienId) =>
  buchungen.filter((b) => b.serienId === serienId);
